import { Group, Progress, ScrollArea, Stack } from '@mantine/core'
import { IconScoreboard } from '@tabler/icons-react'
import { z } from 'zod'
import { Typography } from '../../../../common/typography'
import { useCompetitionId } from '../../../../common/hooks'
import { KpiStat, useMatchStats, useSingleMatch } from '../../../../matches/api'
import MatchScoreCard from '../../../../stories/MatchScoreCard/MatchScoreCard'
import { MatchSelect, TitleInput } from './OptionsFormFields'
import { WidgetEmpty, WidgetSection, WidgetSuspense } from './WidgetShell'
import { DashboardWidget } from './types'

const Schema = z.object({
  title: z.string().default('Match Details'),
  match_id: z.number().nullable().default(null),
})

type Settings = z.infer<typeof Schema>

// Same threshold KpiSummary uses, but with explicit dark-friendly colours so
// the widget reads correctly on the dashboard's dark canvas.
function kpiColor(percentage: number): string {
  return percentage < 80 ? 'red.6' : 'green.6'
}

const TEXT_COLOR = { light: 'gray-3', dark: 'gray-3' } as const

/** Home vs away comparison for a single KPI, styled for the dark dashboard. */
function KpiCompareRow({ stat }: { stat: KpiStat }) {
  const home = stat.home_team_percent * 100
  const away = stat.away_team_percent * 100
  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap" gap="md">
        <Typography fs="xxl" ff="JetBrains Mono" fc={TEXT_COLOR}>
          {home.toFixed(1)}%
        </Typography>
        <Typography fs="xxl" fw={600} fc={TEXT_COLOR}>
          {stat.metric_name}
        </Typography>
        <Typography fs="xxl" ff="JetBrains Mono" fc={TEXT_COLOR}>
          {away.toFixed(1)}%
        </Typography>
      </Group>
      <Group justify="space-between" wrap="nowrap" gap="md" align="center">
        {/* Mirror the home bar so it grows from the centre outwards. */}
        <Progress
          value={home}
          color={kpiColor(home)}
          size="xl"
          style={{ flex: 1, transform: 'scaleX(-1)' }}
        />
        <Typography fs="xxl" ff="JetBrains Mono" fc={TEXT_COLOR}>
          {stat.home_team_score}:{stat.away_team_score}
        </Typography>
        <Progress
          value={away}
          color={kpiColor(away)}
          size="xl"
          style={{ flex: 1 }}
        />
      </Group>
    </Stack>
  )
}

function MatchView({
  competitionId,
  matchId,
}: {
  competitionId: number
  matchId: number
}) {
  const { data: match } = useSingleMatch({
    competition_id: competitionId,
    match_id: matchId,
  })
  const { data: matchStats } = useMatchStats({
    competition_id: competitionId,
    match_id: matchId,
  })

  return (
    <Stack gap="md" h="100%" w="100%" style={{ minHeight: 0 }}>
      {/* MatchScoreCard is height:100% with absolutely-positioned internals,
          so it needs a bounded box; otherwise it eats the whole column and
          leaves no room for the stats. */}
      <div style={{ flex: '0 0 32%', minHeight: 0 }}>
        <MatchScoreCard match={match} />
      </div>
      {matchStats.kpi_stats.length > 0 ? (
        <ScrollArea style={{ flex: 1, minHeight: 0 }}>
          {/* Spread the rows to fill the height so a handful of KPIs don't
              leave the lower half empty; overflow scrolls when there are many. */}
          <Stack gap="xl" h="100%" justify="space-evenly" py="xs">
            {matchStats.kpi_stats.map((kpiStat) => (
              <KpiCompareRow key={kpiStat.metric_id} stat={kpiStat} />
            ))}
          </Stack>
        </ScrollArea>
      ) : (
        <div style={{ flex: 1, minHeight: 0 }}>
          <WidgetEmpty message="No stats yet" />
        </div>
      )}
    </Stack>
  )
}

function Inner({ settings }: { settings: Settings }) {
  const competition_id = useCompetitionId()

  if (settings.match_id == null) {
    return <WidgetEmpty message="Pick a match" />
  }

  return (
    <MatchView competitionId={competition_id} matchId={settings.match_id} />
  )
}

function Component({ settings }: { settings: Settings }) {
  return (
    <WidgetSection title={settings.title} boxed={false}>
      <WidgetSuspense>
        <Inner settings={settings} />
      </WidgetSuspense>
    </WidgetSection>
  )
}

function OptionsForm({
  value,
  onChange,
}: {
  value: Settings
  onChange: (next: Settings) => void
}) {
  const patch = (p: Partial<Settings>) => onChange({ ...value, ...p })
  return (
    <Stack gap="xs">
      <TitleInput value={value.title} onChange={(v) => patch({ title: v })} />
      <MatchSelect
        value={value.match_id}
        onChange={(v) => patch({ match_id: v })}
      />
    </Stack>
  )
}

export const MatchDetailsWidget: DashboardWidget<Settings> = {
  type: 'match_details',
  label: 'Match Details',
  description: 'KPI breakdown for a selected match',
  icon: IconScoreboard,
  minW: 2,
  minH: 3,
  maxW: 2,
  maxH: 3,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  Component,
  OptionsForm,
}
